import { CalendarBlank, X } from '@phosphor-icons/react';
import { format, parse } from 'date-fns';
import { ptBR } from 'react-day-picker/locale';
import { DayPicker } from 'react-day-picker';
import { Popover } from 'radix-ui';
import { useState } from 'react';
import { formatDateLong } from '@/lib/format';
import { cn } from '@/lib/cn';
import 'react-day-picker/style.css';

interface DatePickerProps {
  /** Data civil no formato AAAA-MM-DD (igual ao contrato `isoDate`). */
  value: string | null | undefined;
  onChange: (iso: string | null) => void;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  clearable?: boolean;
  fromDate?: Date;
  toDate?: Date;
  /** Escolha de mês e ano por listas (datas distantes, como nascimento). */
  yearDropdown?: boolean;
  className?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
  'aria-describedby'?: string;
}

const toDate = (iso: string) => parse(iso, 'yyyy-MM-dd', new Date());

/** Seletor de data próprio (popover + calendário pt-BR), nunca <input type="date">. */
export function DatePicker({ value, onChange, placeholder = 'Selecione a data', clearable, fromDate, toDate: to, yearDropdown, className, disabled, ...aria }: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const selected = value ? toDate(value) : undefined;
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <div className="relative">
        <Popover.Trigger asChild>
          <button
            type="button"
            disabled={disabled}
            {...aria}
            className={cn(
              'inline-flex h-10 w-full items-center gap-2 rounded-control border border-input bg-card px-3 text-left text-sm transition-colors hover:border-foreground/50 focus-visible:border-ring focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-ring disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-70 aria-[invalid=true]:border-destructive',
              !value && 'text-muted-foreground/80',
              clearable && value && 'pr-9',
              className,
            )}
          >
            <CalendarBlank className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            <span className="truncate">{value ? formatDateLong(value) : placeholder}</span>
          </button>
        </Popover.Trigger>
        {clearable && value && !disabled && (
          <button
            type="button"
            aria-label="Limpar data"
            onClick={() => onChange(null)}
            className="absolute inset-y-0 right-1.5 my-auto flex size-7 items-center justify-center rounded-control text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="size-4" aria-hidden />
          </button>
        )}
      </div>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className="oc-pop-in z-[70] rounded-panel border bg-card p-2 text-card-foreground shadow-[0_12px_32px_-12px_oklch(0.25_0.04_205/0.35)]"
        >
          <DayPicker
            mode="single"
            locale={ptBR}
            selected={selected}
            defaultMonth={selected}
            captionLayout={yearDropdown ? 'dropdown' : 'label'}
            startMonth={fromDate ?? (yearDropdown ? new Date(1900, 0) : undefined)}
            endMonth={to ?? (yearDropdown ? new Date(new Date().getFullYear() + 10, 11) : undefined)}
            onSelect={(d) => {
              onChange(d ? format(d, 'yyyy-MM-dd') : null);
              setOpen(false);
            }}
            autoFocus
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
